/* eslint-disable @typescript-eslint/no-use-before-define */
import { Icon } from '@zoom/components';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'reactstrap';

import { suppressContextMenuHandler } from '../../../../components/Dropdown/UncontrolledDropdown';
import { AGENT_WORKPLACE_ICON, AGENT_WORKPLACE_REMOTE_BUTTON, AGENT_WORKPLACE_OFFICE_BUTTON } from '../../constants';

interface AgentWorkplaceProps {
  isContextMenuEnabled: boolean;
  remote: boolean;
  onWorkplaceChange: () => void;
  scrollbarContainerRef: HTMLElement | null;
}

export const AgentWorkplace: React.FC<AgentWorkplaceProps> = ({
  isContextMenuEnabled,
  remote,
  onWorkplaceChange,
  scrollbarContainerRef,
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [isContainerAttached, setContainerAttached] = useState(false);
  const { t } = useTranslation();

  const iconRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const hideMenu = () => {
    setMenuOpened(false);
    document.removeEventListener('mousedown', handleMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.removeEventListener('scroll', handleMenuDismiss);
    }
  };

  const handleMenuDismiss = (e: Event) => {
    if (contextMenuRef && contextMenuRef.current && e.target && !contextMenuRef.current.contains(e.target as Node)) {
      hideMenu();
    }
  };

  const menuHandler = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    setMenuOpened(true);
    document.addEventListener('mousedown', handleMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.addEventListener('scroll', handleMenuDismiss);
    }
    return false;
  };

  const handleOnWorkplaceChange = () => {
    onWorkplaceChange();
    hideMenu();
  };

  return (
    <>
      <span
        ref={iconRef}
        className={classnames('agent-workplace__icon', { 'agent-workplace__icon--active': isContextMenuEnabled })}
        data-test-id={AGENT_WORKPLACE_ICON}
        title={remote ? t('schedule.workingRemotely') : t('schedule.workingInOffice')}
      >
        {remote ? (
          <Icon onClick={isContextMenuEnabled ? menuHandler : undefined} kind="house" size={15} color="green" />
        ) : (
          <Icon onClick={isContextMenuEnabled ? menuHandler : undefined} kind="user" size={15} color="blue-light" />
        )}
      </span>
      {isContainerAttached && (
        <Popover
          trigger="click"
          isOpen={menuOpened}
          target={iconRef.current!}
          placement="bottom"
          fade={false}
          boundariesElement="window"
          className="agent-workplace__popover"
          modifiers={{ offset: { enabled: true, offset: '105, 0' } }}
        >
          <div
            className="agent-workplace__popover-content"
            ref={contextMenuRef}
            onContextMenu={suppressContextMenuHandler}
          >
            <button
              className="agent-workplace__popover-button"
              data-test-id={AGENT_WORKPLACE_REMOTE_BUTTON}
              disabled={remote}
              onClick={handleOnWorkplaceChange}
              type="button"
            >
              <Icon color="green" kind="house" onClick={menuHandler} size={15} />
              {t('schedule.workplaceType.remote')}
            </button>
            <button
              className="agent-workplace__popover-button"
              data-test-id={AGENT_WORKPLACE_OFFICE_BUTTON}
              disabled={!remote}
              onClick={handleOnWorkplaceChange}
              type="button"
            >
              <Icon color="blue-light" kind="user" onClick={menuHandler} size={15} />
              {t('schedule.workplaceType.office')}
            </button>
          </div>
        </Popover>
      )}
    </>
  );
};
